/**
 * Seamless looping ticker component.
 * Renders a horizontal strip of messages with configurable divider and colours.
 * Pause on hover (desktop) and on touch (mobile). Uses 6 duplicated sets for seamless loop;
 * when paused, animation stops at current position (no snap). Items include a trailing
 * divider so boundaries between clones render as ...msg|msg... Outer flex gap must
 * match TickerContent inner gap so separators before/after duplicate joins match
 * spacing between message|message inside a set (KINDW-1014).
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';

const DEFAULT_DIVIDER = '|';
const DEFAULT_TEXT_COLOUR = '#4a4a4a';
const DEFAULT_BG_COLOUR = '#ffffff';
const DIVIDER_STROKE_COLOUR = '#32312F';

/** Vertical line divider: 1px wide, 30px tall (design). Stroke uses divider colour. */
function TickerDividerSvg({stroke = DIVIDER_STROKE_COLOUR}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1"
      height="30"
      viewBox="0 0 1 30"
      fill="none"
      aria-hidden
      style={{flexShrink: 0}}
    >
      <path d="M0.5 0V30" stroke={stroke} />
    </svg>
  );
}

TickerDividerSvg.displayName = 'TickerDividerSvg';
TickerDividerSvg.propTypes = {stroke: PropTypes.string};

/** Number of duplicate sets for seamless loop (match demo structure). */
const TICKER_SET_COPIES = 6;

/** Pixels per second for JS-driven scroll. */
const TICKER_SPEED = 50;

/** Chakra md breakpoint (48em). Mobile = below md; desktop = md and up. */
const MEDIA_QUERY_MD = '(min-width: 48em)';

/**
 * LoopingTicker component
 *
 * @param {Object} props - Component props
 * @param {Contentful.LoopingTicker} props.component - LoopingTicker data (messages, dividerCharacter, pauseOnHover, textColourHex, backgroundColourHex, enableAnimation)
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether data is being fetched
 * @returns {React.ReactElement}
 */
export const LoopingTicker = ({
  component,
  isComplete = true,
  isFetching = false,
  ...props
}) => {
  const {
    messages = [],
    divider,
    dividerCharacter,
    pauseOnHover = true,
    textColourHex,
    backgroundColourHex,
    textColour: textColourField,
    backgroundColour: backgroundColourField,
    enableAnimation = false,
  } = component || {};

  const dividerChar = divider ?? dividerCharacter ?? DEFAULT_DIVIDER;

  const textColour = normalizeHex(textColourHex || textColourField, DEFAULT_TEXT_COLOUR);
  const bgColour = normalizeHex(backgroundColourHex || backgroundColourField, DEFAULT_BG_COLOUR);

  const contentRef = useRef(null);
  const translateXRef = useRef(0);
  const rafRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia(MEDIA_QUERY_MD);
    const update = () => setIsMobile(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const items = useMemo(() => {
    const list = Array.isArray(messages) ? messages : [];
    if (list.length === 0) return [];
    const result = [];
    list.forEach((msg, i) => {
      if (i > 0) result.push({type: 'divider', value: dividerChar});
      const text = typeof msg === 'string' ? msg : (msg?.value ?? msg?.text ?? String(msg ?? ''));
      result.push({type: 'message', value: text});
    });
    result.push({type: 'divider', value: dividerChar});
    return result;
  }, [messages, dividerChar]);

  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover) setIsPaused(true);
  }, [pauseOnHover]);

  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover) setIsPaused(false);
  }, [pauseOnHover]);

  const handleTouchStart = useCallback(() => {
    if (pauseOnHover) setIsPaused(true);
  }, [pauseOnHover]);

  const handleTouchEnd = useCallback(() => {
    if (pauseOnHover) setIsPaused(false);
  }, [pauseOnHover]);

  const applyTransform = useCallback((px) => {
    const el = contentRef.current;
    if (el) el.style.transform = `translateX(${px}px)`;
  }, []);

  // JS-driven animation. Mobile: always animate. Desktop: animate only when Enable Animation = Yes.
  useEffect(() => {
    const animate = (isMobile ? true : enableAnimation === true) && items.length > 0;
    if (!animate) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      applyTransform(0);
      translateXRef.current = 0;
      return;
    }

    const el = contentRef.current;
    if (!el) return;

    const totalWidth = el.scrollWidth;
    const setWidth = totalWidth / TICKER_SET_COPIES;
    if (setWidth <= 0) return;

    if (isPaused) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    let lastTime = null;

    const tick = (now) => {
      if (!contentRef.current) return;
      lastTime = lastTime ?? now;
      const delta = (now - lastTime) / 1000;
      lastTime = now;

      let tx = translateXRef.current - TICKER_SPEED * delta;
      if (tx <= -setWidth) tx += setWidth;
      translateXRef.current = tx;
      applyTransform(tx);

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enableAnimation, isMobile, items.length, isPaused, applyTransform]);

  if (!items.length) return null;

  const animateEnabled = (isMobile ? true : enableAnimation === true) && items.length > 0;
  const centerOnDesktop = !isMobile && !animateEnabled;

  return (
    <Box
      width="100%"
      overflow="hidden"
      backgroundColor={bgColour}
      py={6}
      px="48px"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      {...props}
    >
      <Box
        display={{base: 'block', md: centerOnDesktop ? 'flex' : 'block'}}
        justifyContent={{md: centerOnDesktop ? 'center' : undefined}}
        width="100%"
      >
        <Box
          ref={contentRef}
          as="div"
          className="ticker-content"
          display="flex"
          alignItems="center"
          flexWrap="nowrap"
          flex="0 0 auto"
          width="max-content"
          gap={12}
        >
          {animateEnabled
            ? Array.from({length: TICKER_SET_COPIES}, (_, setIndex) => (
                <TickerContent key={setIndex} items={items} textColour={textColour} hideTrailingDivider={false} />
              ))
            : (
              <TickerContent items={items} textColour={textColour} hideTrailingDivider={centerOnDesktop} />
            )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * One set of ticker items (message, divider, message, ..., divider). Duplicated 6x when animating.
 * When hideTrailingDivider is true (static desktop), the last item is not rendered if it is a divider.
 */
function TickerContent({items, textColour, hideTrailingDivider = false}) {
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      flexWrap="nowrap"
      gap={{base: 12, md: 12}}
      flexShrink={0}
    >
      {items.map((item, idx) => {
        if (hideTrailingDivider && idx === items.length - 1 && item.type === 'divider') return null;
        return item.type === 'divider' ? (
          <Box
            key={`d-${idx}`}
            as="span"
            className="ticker-divider"
            display="inline-flex"
            alignItems="center"
            aria-hidden
          >
            <TickerDividerSvg stroke={DIVIDER_STROKE_COLOUR} />
          </Box>
        ) : (
          <Text
            key={`m-${idx}`}
            as="span"
            className="ticker-item"
            flexShrink={0}
            fontFamily="body"
            fontSize="xl"
            fontWeight={400}
            lineHeight={1.5}
            textAlign="center"
            color={textColour}
            whiteSpace="nowrap"
          >
            {item.value}
          </Text>
        );
      })}
    </Box>
  );
}

TickerContent.displayName = 'TickerContent';
TickerContent.propTypes = {
  hideTrailingDivider: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['message', 'divider']).isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  textColour: PropTypes.string.isRequired,
};

LoopingTicker.displayName = 'LoopingTicker';
LoopingTicker.propTypes = {
  component: PropTypes.shape({
    messages: PropTypes.arrayOf(PropTypes.string),
    divider: PropTypes.string,
    dividerCharacter: PropTypes.string,
    pauseOnHover: PropTypes.bool,
    textColour: PropTypes.string,
    textColourHex: PropTypes.string,
    backgroundColour: PropTypes.string,
    backgroundColourHex: PropTypes.string,
    enableAnimation: PropTypes.bool,
  }).isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
